import { ReactNode } from 'react';
import type { AttendanceStatus } from '../../types/attendance';
import { formatTimeInBusinessTZ } from '../../utils/date';

/**
 * What a record can say about how a session went, from either surface that holds one.
 *
 * A structural type rather than `AttendanceRecord`, because the archive's rows come back
 * from an RPC and carry no enrollment or program id — they are the same nine facts under
 * the same nine names, which is all this component reads.
 */
export interface AttendanceFacts {
  status: AttendanceStatus;
  checkedInAt: string | null;
  checkedInBy: string | null;
  checkedInByName: string | null;
  pickedUpAt: string | null;
  pickedUpByName: string | null;
  pickupIsAuthorized: boolean | null;
  pickupRecordedByName: string | null;
  /**
   * Who wrote the check-out. Optional because the archive's RPC rows do not carry it; absent
   * reads as a staff check-out, which every row before parent self check-out was.
   */
  checkedOutBy?: string | null;
  absenceReason: string | null;
}

/** A timestamptz rendered as a business-timezone wall clock, e.g. "3:12 PM". */
export function formatClockTime(iso: string): string {
  return formatTimeInBusinessTZ(iso, { hour12: true });
}

/**
 * What was recorded for this student, one fact per line.
 *
 * These used to be concatenated into a single line, which on a phone truncated to
 * something like "Checked out by grade 吗 at 09:33 PM ⚠ N…". Each fact now gets its own
 * row and is allowed to wrap, so nothing is hidden behind an ellipsis.
 *
 * A checked-out student keeps the whole day's sequence — checked in, checked out, and by
 * whom — because "when did they arrive" is still a question worth answering after they
 * have gone home, and it is most of what the archive is asked.
 *
 * Whether there is a collector to name is read off the record rather than passed in: a class
 * check-out leaves `pickedUpByName` null, and an after-school row written before enrichment
 * classes existed still has its name. Both render correctly without knowing the mode.
 */
export function AttendanceRecordFacts({ record }: { record?: AttendanceFacts | null }) {
  if (!record) return null;

  const { status } = record;
  const lines: { key: string; node: ReactNode }[] = [];

  // The check-in is still part of the day's story after a student has gone home, so it
  // stays on the row rather than being replaced by the check-out.
  if (record.checkedInAt && (status === 'checked_in' || status === 'picked_up')) {
    const who = record.checkedInBy === 'parent' ? 'parent' : record.checkedInByName || 'teacher';
    lines.push({
      key: 'checked-in',
      node: <>Checked in by {who} · {formatClockTime(record.checkedInAt)}</>,
    });
  }

  if (status === 'picked_up') {
    const at = record.pickedUpAt ? ` · ${formatClockTime(record.pickedUpAt)}` : '';

    if (record.pickedUpByName) {
      // An unrecognized collector is marked the way a word processor marks a questionable
      // word — a dotted underline you notice on a second look. The teacher has already
      // handed the child over by this point, so an alarm here would only frighten; the
      // point is to leave a reviewable trace, not to shout.
      const collector = record.pickupIsAuthorized === false
        ? (
          <span
            className="underline decoration-dotted decoration-amber-500 underline-offset-2"
            title="Not on this student's authorized pickup list"
          >
            {record.pickedUpByName}
          </span>
        )
        : record.pickedUpByName;

      lines.push({ key: 'checked-out', node: <>Checked out to {collector}{at}</> });
    } else if (record.pickedUpAt) {
      // A class that simply ended. Nothing is missing from this row — there was no adult.
      lines.push({ key: 'checked-out', node: <>Checked out{at}</> });
    }

    if (record.pickupRecordedByName) {
      lines.push({ key: 'released-by', node: <>Released by {record.pickupRecordedByName}</> });
    }
    // No staff member released this child, so there is nobody to name — but a teacher reading
    // the row needs to know the check-out was a parent's tap, not a handover they witnessed.
    if (record.checkedOutBy === 'parent') {
      lines.push({ key: 'self-checkout', node: <>Parent checked out in the app</> });
    }
  }

  if (status === 'absent' && record.absenceReason) {
    lines.push({ key: 'absence-reason', node: <>Absent — {record.absenceReason}</> });
  }

  if (lines.length === 0) return null;

  return (
    <div className="mt-1 space-y-0.5">
      {lines.map(line => (
        <p key={line.key} className="text-xs leading-snug text-gray-500">
          {line.node}
        </p>
      ))}
    </div>
  );
}
